import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, LayoutDashboard, User as UserIcon, Menu, Download } from "lucide-react";
import { useInstall } from "@/context/InstallContext";

const nav = [
  { to: "/browse", label: "Browse" },
  { to: "/browse?category=catering", label: "Catering" },
  { to: "/browse?category=venue", label: "Venues" },
  { to: "/browse?category=photography", label: "Photography" },
  { to: "/browse?category=transportation", label: "Transport" },
  { to: "/browse?category=florist", label: "Florists" },
  { to: "/browse?category=dj", label: "DJs" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { canInstall, showPrompt, isStandalone } = useInstall();
  const navigate = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const dashPath =
    user?.role === "admin" ? "/admin"
      : user?.role === "vendor" ? "/vendor/dashboard"
      : "/planner/dashboard";

  const closeAndGo = (path) => { setOpen(false); navigate(path); };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--bg)]/85 border-b border-[color:var(--border)]"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0">
          <img src="/ncontro-logo.png" alt="NContro" className="h-7 sm:h-9 w-auto" />
          <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Est. 2026</span>
        </Link>

        {/* Desktop nav — marketplace browsing is planner/public only. Vendors
            do not browse other vendors, so the entire nav row is suppressed. */}
        {user?.role !== "vendor" && (
          <nav className="hidden lg:flex items-center gap-6">
            {nav.slice(0, 6).map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-sm font-medium link-underline text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Desktop auth */}
          <div className="hidden lg:flex items-center gap-3">
            {!user ? (
              <>
                <Button variant="ghost" data-testid="header-signin-btn" onClick={() => navigate("/login")} className="text-sm">
                  Sign in
                </Button>
                <Button data-testid="header-getstarted-btn" onClick={() => navigate("/register")} className="btn-sage rounded-full px-6 h-10">
                  Get started
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-3 rounded-full border border-[color:var(--border-strong)] pl-2 pr-4 py-1.5 bg-white hover:bg-[color:var(--surface)] transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full bg-[color:var(--accent)] text-white flex items-center justify-center text-xs font-semibold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <span className="text-sm">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {user.name}
                      {user.role === "admin" && (
                        <span data-testid="header-admin-pill" className="text-[9px] uppercase tracking-widest bg-[color:var(--accent)] text-white px-1.5 py-0.5 rounded-full">Admin</span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C5C5C]">{user.email}</div>
                    <div className="text-[10px] mt-1 uppercase tracking-widest text-[color:var(--accent)]">{user.role}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(dashPath)}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {user.role === "admin" ? "Admin dashboard" : "Dashboard"}
                  </DropdownMenuItem>
                  {canInstall && !isStandalone && (
                    <DropdownMenuItem data-testid="menu-install-app" onClick={showPrompt}>
                      <Download className="w-4 h-4 mr-2" /> Install app
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile — small avatar or CTA + menu */}
          {user ? (
            <button
              data-testid="mobile-avatar-btn"
              onClick={() => navigate(dashPath)}
              className="lg:hidden w-9 h-9 rounded-full bg-[color:var(--accent)] text-white flex items-center justify-center text-xs font-semibold"
              aria-label="Dashboard"
            >
              {user.name?.[0]?.toUpperCase() || "U"}
            </button>
          ) : (
            <Button
              data-testid="mobile-signin-btn"
              onClick={() => navigate("/login")}
              variant="ghost"
              className="lg:hidden text-sm h-9 px-3"
            >
              Sign in
            </Button>
          )}

          {/* Hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="lg:hidden w-10 h-10 rounded-full border border-[color:var(--border-strong)] flex items-center justify-center bg-white"
              >
                <Menu className="w-4 h-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm bg-[#FDFBF7] border-l-[#E8E2D9] p-6">
              <div className="mt-2 mb-8">
                <img src="/ncontro-logo.png" alt="NContro" className="h-8 w-auto" />
                <div className="eyebrow mt-2">Menu</div>
              </div>
              <nav className="space-y-4">
                {user?.role !== "vendor" && nav.map((n) => (
                  <button
                    key={n.label}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    onClick={() => closeAndGo(n.to)}
                    className="block w-full text-left font-serif text-2xl border-b border-[#E8E2D9] pb-3"
                  >
                    {n.label}
                  </button>
                ))}
              </nav>
              {canInstall && !isStandalone && (
                <button
                  data-testid="mobile-menu-install-app"
                  onClick={() => { setOpen(false); showPrompt(); }}
                  className="mt-6 w-full flex items-center justify-between gap-3 rounded-2xl border border-[#F97316]/25 bg-[#F97316]/5 hover:bg-[#F97316]/10 active:bg-[#F97316]/15 px-4 py-3 text-left transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-white ring-1 ring-[#F97316]/20 flex items-center justify-center overflow-hidden">
                      <img src="/icons/icon-192.png" alt="" className="w-full h-full object-cover" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-neutral-900">Install NContro</span>
                      <span className="block text-xs text-neutral-600">Add to your home screen</span>
                    </span>
                  </span>
                  <Download className="w-4 h-4 text-[#F97316]" />
                </button>
              )}
              <div className="mt-8 space-y-3">
                {!user ? (
                  <>
                    <Button data-testid="mobile-menu-signin" onClick={() => closeAndGo("/login")} variant="outline" className="w-full h-12 rounded-full border-[#E8E2D9]">
                      Sign in
                    </Button>
                    <Button data-testid="mobile-menu-getstarted" onClick={() => closeAndGo("/register")} className="btn-sage w-full rounded-full h-12">
                      Get started
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="text-sm">{user.name}</div>
                    <div className="text-xs text-[#5C5C5C]">{user.email}</div>
                    <div className="eyebrow text-[#8A9A86]">{user.role}</div>
                    <Button data-testid="mobile-menu-dashboard" onClick={() => closeAndGo(dashPath)} className="btn-sage w-full rounded-full h-12">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </Button>
                    <Button data-testid="mobile-menu-logout" onClick={async () => { await logout(); closeAndGo("/"); }} variant="outline" className="w-full h-12 rounded-full border-[#E8E2D9]">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mobile horizontal category scroller — hidden for vendor accounts. */}
      {user?.role !== "vendor" && (
        <div
          data-testid="mobile-category-scroller"
          className="lg:hidden border-t border-[color:var(--border)] bg-[color:var(--bg)]/85 backdrop-blur-xl"
        >
          <div
            className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center gap-2 overflow-x-auto py-3 no-scrollbar"
            style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          >
            {nav.map((n) => {
              const isBrowseAll = n.label === "Browse";
              const targetCat = n.to.includes("category=") ? n.to.split("category=")[1] : null;
              const isActive = isBrowseAll
                ? loc.pathname === "/browse" && !searchParams.get("category")
                : loc.pathname === "/browse" && searchParams.get("category") === targetCat;
              return (
                <button
                  key={n.label}
                  data-testid={`scroll-nav-${n.label.toLowerCase()}`}
                  onClick={() => navigate(n.to)}
                  className={`shrink-0 px-4 h-9 rounded-full border text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[color:var(--text)] text-white border-[color:var(--text)]"
                      : "bg-white text-[color:var(--text)] border-[color:var(--border-strong)] hover:bg-[color:var(--surface)]"
                  }`}
                >
                  {n.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
